import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Comment, ConversationAPI } from 'api/conversations';
import { ProjectId } from 'api/models';
import { ConversationPane } from 'components/organisms/conversations';
import { useProjectDetail } from 'data/projects';
import { useAccount } from 'hooks/use-account';
import { useGetConversationWithWS } from 'hooks/use-conversations';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { useActiveGlobalConversation } from '../../proj-conversation-hooks';
import { GlobalConversationDetails } from '../channel-details';
import { CommentSeenBy } from './comment-seen-by';
import { MessageFooter } from './message-footer';
import { ConvPaneWrapper } from './styles';

interface Props {
  projectId: ProjectId;
  isUserMemberOfChannel?: boolean;
}

export const ProjectGlobalConversationPane: FC<Props> = ({
  projectId,
  isUserMemberOfChannel,
}) => {
  const {
    user: { userId },
  } = useUser();
  const { activeConversation } = useActiveGlobalConversation();
  const { data: project } = useProjectDetail(projectId);
  const commentBoxPlaceholder = useTranslation(
    'projects.conversations.placeholder',
    {
      conversationName: activeConversation?.entityName || '',
      projectName: project?.projectName,
    },
  );

  const { accountId } = useAccount();
  const [userComments, setUserComments] = useState<Comment[]>([]);
  const context = activeConversation
    ? {
        type: activeConversation.contextType,
        id: activeConversation.entityId,
      }
    : undefined;
  const { data: convData } = useGetConversationWithWS(projectId[1], context);
  const members = useMemo(() => convData?.members, [convData]);

  const memberDetails = (members || []).find(
    (memberObject) => memberObject.user?.userId === userId,
  );

  const currentLastReadCommentId = memberDetails?.lastSeenCommentCreatedAt;
  // Store the older commentId between renders so that when currentLastReadCommentId changes through socket,
  // we can still refer the old lastReadCommentId.
  const [lastReadComment, setLastComment] =
    useState<
      | undefined
      | {
          commentId: number;
          convId: number | undefined;
        }
    >();

  useEffect(() => {
    if (!currentLastReadCommentId) {
      return;
    }

    if (
      !lastReadComment?.commentId ||
      lastReadComment?.convId !== activeConversation?.entityId
    ) {
      setLastComment({
        commentId: currentLastReadCommentId,
        convId: activeConversation?.entityId,
      });
    }
  }, [currentLastReadCommentId, lastReadComment, activeConversation?.entityId]);

  const currentUserLastReadCommentIndex = userComments.findIndex(
    (cmnt) => cmnt.commentId === lastReadComment?.commentId,
  );
  const tailMessages = userComments.slice(currentUserLastReadCommentIndex + 1);
  // No need to show the hook if the lastMessages were from the activeUser itself
  const shouldRenderNewMessagesMarker = tailMessages.find(
    (cmnt) => cmnt.user.userId !== userId,
  );

  const lastComment = userComments[userComments.length - 1];

  const onConversationCommentsLoaded = useCallback((comments: Comment[]) => {
    if (comments.length) {
      setUserComments(comments);
    }
  }, []);

  const renderMessageFooterExtra = useCallback(
    (comment) =>
      shouldRenderNewMessagesMarker &&
      lastComment?.commentId !== lastReadComment?.commentId &&
      currentUserLastReadCommentIndex > -1 ? (
        <MessageFooter
          currentComment={comment}
          lastReadCommentId={lastReadComment?.commentId}
        />
      ) : null,
    [
      lastReadComment,
      lastComment,
      shouldRenderNewMessagesMarker,
      currentUserLastReadCommentIndex,
    ],
  );

  const renderMessageTailComponent = useCallback(
    (currentComment: Comment) => {
      if (!members) {
        return null;
      }

      const seenUsers =
        members.filter(
          (member) =>
            member?.user &&
            userId !== member?.user?.userId &&
            currentComment?.commentId &&
            currentComment?.createdAt === member?.lastSeenCommentCreatedAt,
        ) ?? [];

      return seenUsers.length ? <CommentSeenBy seenUsers={seenUsers} /> : null;
    },
    [members, userId],
  );

  return (
    <ConvPaneWrapper isUserMemberOfChannel={Boolean(isUserMemberOfChannel)}>
      {activeConversation?.entityId ? (
        <ConversationPane
          onStarMessage={() => {}}
          spotlights={[]}
          messagesInitialComponent={
            <GlobalConversationDetails
              activeConversation={activeConversation}
              projectId={projectId}
            />
          }
          renderMessageFooterExtra={renderMessageFooterExtra}
          renderMessageTailComponent={renderMessageTailComponent}
          onCommentsLoaded={onConversationCommentsLoaded}
          commentBoxConfig={{
            className: 'project-conversation-comment-box',
            placeholder: commentBoxPlaceholder,
            hidePrivate: activeConversation.private,
          }}
          context={context!}
          projectId={projectId[1]}
          accountId={accountId}
          apiService={ConversationAPI}
          hideAuditLog
        />
      ) : null}
    </ConvPaneWrapper>
  );
};
